import React from 'react';
import { Settings, Disc, Zap, Lock } from 'lucide-react';
import { useCutList, ALGORITHM_INFO } from '../../contexts/CutListContext';
import { useAuth } from '../../contexts/AuthContext';
import { UNIT_SYSTEMS } from '../../utils/units';

/**
 * Step 1: Setup - Configure units, blade kerf, and algorithm
 * Sheet sizes are now configured per stock type in the Parts step
 */
const SetupStep = ({ onNext }) => {
  const {
    cutWidth,
    setCutWidth,
    trimFactoryEdges,
    setTrimFactoryEdges,
    trimAmount,
    setTrimAmount,
    currentUnits,
    handleUnitChange,
    algorithm,
    setAlgorithm,
  } = useCutList();

  const { canUseAlgorithm, promptUpgrade } = useAuth();

  const getUnitLabel = () => {
    if (currentUnits.system === UNIT_SYSTEMS.IMPERIAL) {
      return currentUnits.unit === 'inches' ? 'in' : 'ft';
    }
    return currentUnits.unit;
  };

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="text-center py-4">
        <Settings className="w-12 h-12 mx-auto text-sage-600 mb-2" />
        <h1 className="text-2xl font-bold text-gray-900">Cut Settings</h1>
        <p className="text-gray-500 mt-1">Configure units, kerf, and optimization</p>
      </div>

      {/* Unit System */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Measurement System</h2>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => handleUnitChange({ system: UNIT_SYSTEMS.IMPERIAL, unit: 'inches' })}
            className={`p-4 rounded-xl border-2 transition-colors ${
              currentUnits.system === UNIT_SYSTEMS.IMPERIAL
                ? 'border-accent-400 bg-accent-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <span className="text-2xl mb-1 block">🇺🇸</span>
            <span className="font-semibold">Imperial</span>
            <span className="text-xs text-gray-500 block">inches / feet</span>
          </button>
          <button
            onClick={() => handleUnitChange({ system: UNIT_SYSTEMS.METRIC, unit: 'cm' })}
            className={`p-4 rounded-xl border-2 transition-colors ${
              currentUnits.system === UNIT_SYSTEMS.METRIC
                ? 'border-accent-400 bg-accent-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <span className="text-2xl mb-1 block">🇪🇺</span>
            <span className="font-semibold">Metric</span>
            <span className="text-xs text-gray-500 block">mm / cm</span>
          </button>
        </div>
      </div>

      {/* Blade Kerf */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Disc className="w-4 h-4 text-gray-500" />
          <h2 className="text-sm font-semibold text-gray-700">Blade Kerf (Cut Width)</h2>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => setCutWidth(Math.max(0, cutWidth - 0.01))}
            className="w-12 h-12 rounded-full bg-gray-100 text-xl font-bold text-gray-700 hover:bg-gray-200"
          >
            −
          </button>
          <div className="flex-1 text-center">
            <span className="text-2xl font-bold text-gray-900">
              {cutWidth.toFixed(3)}
            </span>
            <span className="text-gray-500 ml-1">{getUnitLabel()}</span>
          </div>
          <button
            onClick={() => setCutWidth(cutWidth + 0.01)}
            className="w-12 h-12 rounded-full bg-gray-100 text-xl font-bold text-gray-700 hover:bg-gray-200"
          >
            +
          </button>
        </div>
        <p className="text-xs text-gray-400 text-center mt-2">
          Standard table saw: ~3mm / 1/8"
        </p>

        {/* Trim Factory Edges */}
        <label className="flex items-center gap-3 cursor-pointer mt-4">
          <input
            type="checkbox"
            checked={trimFactoryEdges}
            onChange={(e) => setTrimFactoryEdges(e.target.checked)}
            className="w-5 h-5 rounded border-gray-300 text-accent-500 focus:ring-accent-400"
          />
          <span className="text-sm text-gray-700">Trim Factory Edges</span>
        </label>

        {trimFactoryEdges && (
          <div className="mt-3">
            <label className="text-xs text-gray-500 block mb-2">Trim Amount (per edge)</label>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setTrimAmount(Math.max(0, trimAmount - 0.0625))}
                className="w-10 h-10 rounded-full bg-gray-100 text-lg font-bold text-gray-700 hover:bg-gray-200"
              >
                −
              </button>
              <div className="flex-1 text-center">
                <span className="text-xl font-bold text-gray-900">
                  {trimAmount.toFixed(3)}
                </span>
                <span className="text-gray-500 ml-1">{getUnitLabel()}</span>
              </div>
              <button
                onClick={() => setTrimAmount(trimAmount + 0.0625)}
                className="w-10 h-10 rounded-full bg-gray-100 text-lg font-bold text-gray-700 hover:bg-gray-200"
              >
                +
              </button>
            </div>
            <p className="text-xs text-gray-400 text-center mt-2">
              Amount trimmed from each edge of the sheet
            </p>
          </div>
        )}
      </div>

      {/* Algorithm Selection */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4 text-gray-500" />
          <h2 className="text-sm font-semibold text-gray-700">Optimization Mode</h2>
        </div>

        <div className="space-y-2">
          {Object.entries(ALGORITHM_INFO).map(([key, info]) => {
            const isAvailable = canUseAlgorithm(key);
            const isSelected = algorithm === key;

            return (
              <button
                key={key}
                onClick={() => {
                  if (isAvailable) {
                    setAlgorithm(key);
                  } else {
                    promptUpgrade(`Upgrade to Pro to use the ${info.name} algorithm for better material efficiency.`);
                  }
                }}
                className={`w-full text-left p-3 rounded-xl border-2 transition-colors ${
                  isSelected
                    ? 'border-accent-400 bg-accent-50'
                    : isAvailable
                    ? 'border-gray-200 hover:border-gray-300'
                    : 'border-gray-200 bg-gray-50 opacity-75'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${isAvailable ? 'text-gray-900' : 'text-gray-500'}`}>
                      {info.name}
                    </span>
                    {!isAvailable && <Lock className="w-3.5 h-3.5 text-gray-400" />}
                  </div>
                  <div className="flex items-center gap-2">
                    {!isAvailable && (
                      <span className="text-xs px-1.5 py-0.5 bg-accent-100 text-accent-600 rounded font-medium">
                        PRO
                      </span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      isSelected ? 'bg-accent-400 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {info.efficiency}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">{info.description}</p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SetupStep;
